import React, { FC, ChangeEvent, useState, useEffect } from 'react';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { DentistDirectoryFilter } from 'models/client-directory';
import { useTranslation } from 'react-i18next';
import { DentistType } from 'models/user';
import { useQuery } from '@apollo/client';
import { getParentClinicConnectedWithLabTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Organization } from 'models/organization';

type DentistDirectoryPageActionsProps = {
    onFilterChange: (dentistDirectoryFilter: DentistDirectoryFilter) => void;
    dentistListFilter: DentistDirectoryFilter;
};
const DentistDirectoryPageActions: FC<DentistDirectoryPageActionsProps> = ({
    onFilterChange,
    dentistListFilter,
}: DentistDirectoryPageActionsProps) => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const [dentistDirectoryFilter, setDentistDirectoryFilter] = useState<DentistDirectoryFilter>({
        freeText: '',
        connectedType: DentistType.ALL,
        clinic: 'ALL',
    });
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { parentClinicsForLab } = {}, loading } = useQuery(getParentClinicConnectedWithLabTemplate, {
        variables: {
            filters: {
                isPickUpAndDeliveryPlace: true,
                showParents: true,
            },
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setDentistDirectoryFilter(dentistListFilter);
    }, [dentistListFilter]);

    const updateFilter = (updates: Partial<DentistDirectoryFilter>) => {
        const newFilter = { ...dentistDirectoryFilter, ...updates };
        setDentistDirectoryFilter(newFilter);
        onFilterChange(newFilter);
    };

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateFilter({ freeText: event.target.value });
    };

    const handleTypeOfConnectionChange = (event: SelectChangeEvent) => {
        updateFilter({ connectedType: event.target.value as DentistType });
    };

    const handleClinicChange = (event: SelectChangeEvent) => {
        updateFilter({ clinic: event.target.value });
    };

    return (
        <Box
            sx={{
                marginTop: 1,
                marginBottom: 2,
                bgcolor: 'background.paper',
                padding: 2,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Grid container spacing={1}>
                <Grid item md={6} xs={12}>
                    <TextField
                        id="filter-name"
                        fullWidth
                        onChange={handleSearchChange}
                        value={dentistDirectoryFilter.freeText || ''}
                        label={translate('client-directory:SEARCH_DENTIST')}
                        placeholder={translate('client-directory:SEARCH_DENTIST_PLACEHOLDER')}
                        margin="dense"
                    />
                </Grid>
                <Grid item md={3} xs={12}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>{translate('common:CLINIC')}</InputLabel>
                        <Select
                            id="filter-clinic"
                            value={dentistDirectoryFilter.clinic || 'ALL'}
                            label={translate('common:CLINIC')}
                            onChange={handleClinicChange}
                        >
                            <MenuItem value="ALL">{translate('common:ALL')}</MenuItem>
                            {!loading &&
                                parentClinicsForLab?.map((clinic: Organization) => (
                                    <MenuItem key={clinic._id} value={clinic._id}>
                                        {clinic.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item md={3} xs={12}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>{translate('client-directory:DENTIST_TYPE')}</InputLabel>
                        <Select
                            id="filter-dentist-type"
                            label={translate('client-directory:DENTIST_TYPE')}
                            onChange={handleTypeOfConnectionChange}
                            value={dentistDirectoryFilter.connectedType || DentistType.ALL}
                        >
                            {Object.values(DentistType).map((option) => (
                                <MenuItem key={option} value={option}>
                                    {translate(`client-directory:${option}`)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DentistDirectoryPageActions;
